// src/pages/InterviewAssistant/ProjectiveInterview.tsx
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Target, BarChart2, Award
} from 'lucide-react';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip
} from 'recharts';

// ------------------- данные шкал и проективных вопросов -------------------
const scales = [
  {
    title: 'Ответственность',
    description: 'Готовность брать на себя обязательства и отвечать за результат',
    questions: [
      {
        id: 1,
        text: 'Представьте, что ваш коллега допустил серьёзную ошибку, но вину пытаются возложить на вас. Ваши действия?',
        options: [
          { value: 1, label: 'Промолчу, чтобы не накалять обстановку' },
          { value: 2, label: 'Переведу стрелки на коллегу' },
          { value: 3, label: 'Постараюсь объяснить ситуацию руководителю' },
          { value: 4, label: 'Возьму ответственность, но укажу на реальные причины' },
          { value: 5, label: 'Открыто обсужу с коллегой и вместе доложим руководству' }
        ]
      },
      {
        id: 2,
        text: 'Если проект провалился, что вы чувствуете и делаете в первую очередь?',
        options: [
          { value: 1, label: 'Обвиняю обстоятельства' },
          { value: 2, label: 'Ищу виноватых' },
          { value: 3, label: 'Анализирую свою роль в неудаче' },
          { value: 4, label: 'Делаю выводы и предлагаю план исправления' },
          { value: 5, label: 'Беру ответственность и мобилизую команду на исправление' }
        ]
      }
    ]
  },
  {
    title: 'Инициативность',
    description: 'Способность действовать проактивно, предлагать идеи и решения',
    questions: [
      {
        id: 3,
        text: 'Вы заметили, что процесс можно улучшить, но это не входит в ваши обязанности. Что сделаете?',
        options: [
          { value: 1, label: 'Ничего, это не моя работа' },
          { value: 2, label: 'Расскажу коллегам, может кто-то займётся' },
          { value: 3, label: 'Предложу идею руководителю' },
          { value: 4, label: 'Подготовлю мини-презентацию и обосную выгоду' },
          { value: 5, label: 'Запущу пилот и покажу результат' }
        ]
      },
      {
        id: 4,
        text: 'Как вы обычно реагируете, когда видите проблему, которую никто не решает?',
        options: [
          { value: 1, label: 'Жду, пока заметят другие' },
          { value: 2, label: 'Обсуждаю с коллегами в курилке' },
          { value: 3, label: 'Сообщаю ответственному лицу' },
          { value: 4, label: 'Предлагаю решение и вызываюсь помочь' },
          { value: 5, label: 'Самостоятельно начинаю решать, информируя заинтересованных' }
        ]
      }
    ]
  },
  {
    title: 'Честность',
    description: 'Склонность давать правдивую информацию, даже если она неудобна',
    questions: [
      {
        id: 5,
        text: 'Клиент просит приукрасить результаты в отчёте. Ваша реакция?',
        options: [
          { value: 1, label: 'Соглашусь, клиент всегда прав' },
          { value: 2, label: 'Немного подкорректирую, но без фанатизма' },
          { value: 3, label: 'Объясню, что это некорректно' },
          { value: 4, label: 'Предложу альтернативный способ подачи информации' },
          { value: 5, label: 'Твёрдо откажусь, даже если потеряю клиента' }
        ]
      },
      {
        id: 6,
        text: 'Коллега просит подтвердить его ложную версию событий перед начальством. Ваши действия?',
        options: [
          { value: 1, label: 'Подтвержу, чтобы выручить' },
          { value: 2, label: 'Скажу, что не помню деталей' },
          { value: 3, label: 'Откажусь, но без объяснений' },
          { value: 4, label: 'Объясню коллеге, почему это неправильно' },
          { value: 5, label: 'Расскажу правду, но поддержу коллегу в поиске конструктивного решения' }
        ]
      }
    ]
  },
  {
    title: 'Отношение к неудачам',
    description: 'Способность конструктивно воспринимать критику и поражения',
    questions: [
      {
        id: 7,
        text: 'Ваш проект раскритиковали на общем собрании. Что вы чувствуете и делаете?',
        options: [
          { value: 1, label: 'Обижаюсь и замыкаюсь' },
          { value: 2, label: 'Оправдываюсь' },
          { value: 3, label: 'Выслушиваю и беру паузу' },
          { value: 4, label: 'Анализирую критику и задаю уточняющие вопросы' },
          { value: 5, label: 'Благодарю за обратную связь и сразу предлагаю улучшения' }
        ]
      },
      {
        id: 8,
        text: 'Что для вас важнее: избежать ошибки или достичь цели?',
        options: [
          { value: 1, label: 'Точно избежать ошибки' },
          { value: 2, label: 'Скорее избежать ошибки' },
          { value: 3, label: 'В зависимости от ситуации' },
          { value: 4, label: 'Скорее достичь цели' },
          { value: 5, label: 'Цель оправдывает риск ошибок' }
        ]
      }
    ]
  },
  {
    title: 'Работа в команде (проективная)',
    description: 'Глубинные установки на сотрудничество, доверие, коллективизм',
    questions: [
      {
        id: 9,
        text: 'Опишите идеального коллегу тремя словами.',
        options: [
          { value: 1, label: 'Умный, быстрый, независимый' },
          { value: 2, label: 'Исполнительный, тихий, покладистый' },
          { value: 3, label: 'Надёжный, компетентный, дружелюбный' },
          { value: 4, label: 'Инициативный, поддерживающий, открытый' },
          { value: 5, label: 'Вдохновляющий, честный, взаимовыручающий' }
        ]
      },
      {
        id: 10,
        text: 'Если команда не успевает к дедлайну, а ваша часть уже готова, что вы сделаете?',
        options: [
          { value: 1, label: 'Уйду домой, моя работа сделана' },
          { value: 2, label: 'Предложу помощь, если попросят' },
          { value: 3, label: 'Спрошу, чем могу помочь' },
          { value: 4, label: 'Активно включусь в общую работу' },
          { value: 5, label: 'Организую перераспределение задач и поддержу команду морально' }
        ]
      }
    ]
  },
  {
    title: 'Мотивация достижения',
    description: 'Внутреннее стремление к success, амбициозность, настойчивость',
    questions: [
      {
        id: 11,
        text: 'Что для вас является лучшей наградой за хорошо выполненную работу?',
        options: [
          { value: 1, label: 'Деньги' },
          { value: 2, label: 'Похвала руководителя' },
          { value: 3, label: 'Признание коллег' },
          { value: 4, label: 'Чувство гордости за результат' },
          { value: 5, label: 'Новые возможности для роста и развития' }
        ]
      },
      {
        id: 12,
        text: 'Если задача кажется невыполнимой, что вас мотивирует продолжать?',
        options: [
          { value: 1, label: 'Ничего, брошу' },
          { value: 2, label: 'Обязательства перед другими' },
          { value: 3, label: 'Желание доказать, что смогу' },
          { value: 4, label: 'Азарт и интерес к сложной задаче' },
          { value: 5, label: 'Внутренняя уверенность, что любую проблему можно решить' }
        ]
      }
    ]
  },
  {
    title: 'Отношение к правилам',
    description: 'Гибкость в следовании инструкциям, способность обходить препятствия',
    questions: [
      {
        id: 13,
        text: 'Вы видите, что строгое соблюдение инструкции вредит делу. Ваши действия?',
        options: [
          { value: 1, label: 'Буду следовать инструкции, это безопаснее' },
          { value: 2, label: 'Пожалуюсь, что инструкция устарела' },
          { value: 3, label: 'Обсужу с руководителем возможность отступления' },
          { value: 4, label: 'Отступлю, но задокументирую причины' },
          { value: 5, label: 'Предложу изменить инструкцию, обосновав эффективностью' }
        ]
      },
      {
        id: 14,
        text: 'Как вы относитесь к неформальным договорённостям в обход регламентов?',
        options: [
          { value: 1, label: 'Категорически против' },
          { value: 2, label: 'Не приветствую, но допускаю' },
          { value: 3, label: 'Нейтрально, смотря по ситуации' },
          { value: 4, label: 'Иногда это полезно для дела' },
          { value: 5, label: 'Главное — результат, формальности вторичны' }
        ]
      }
    ]
  },
  {
    title: 'Эмпатия',
    description: 'Способность понимать чувства других, эмоциональный интеллект',
    questions: [
      {
        id: 15,
        text: 'Коллега выглядит расстроенным, но говорит, что всё в порядке. Что вы сделаете?',
        options: [
          { value: 1, label: 'Поверю на слово и пройду мимо' },
          { value: 2, label: 'Спрошу формально, не нужна ли помощь' },
          { value: 3, label: 'Постараюсь аккуратно выяснить причину' },
          { value: 4, label: 'Предложу поговорить в неформальной обстановке' },
          { value: 5, label: 'Окажу поддержку и предложу конкретную помощь, если нужно' }
        ]
      },
      {
        id: 16,
        text: 'Опишите ситуацию, когда вы помогли человеку, хотя вас об этом не просили.',
        options: [
          { value: 1, label: 'Не припомню такого' },
          { value: 2, label: 'Было пару раз, но не уверен в результате' },
          { value: 3, label: 'Да, иногда замечаю потребности и помогаю' },
          { value: 4, label: 'Регулярно проявляю инициативу в помощи' },
          { value: 5, label: 'Это моя естественная реакция, часто получаю благодарность' }
        ]
      }
    ]
  }
];

const interpretation: Record<string, { low: string; medium: string; high: string }> = {
  Ответственность: {
    low: 'Возможно, вы избегаете лишней ответственности. Рекомендуется развивать проактивность и готовность отвечать за свои действия.',
    medium: 'Вы ответственный человек, но иногда можете колебаться в сложных ситуациях. Работайте над уверенностью.',
    high: 'Высокая ответственность — ваше сильное качество. Вы надёжны и на вас можно положиться в любой ситуации.'
  },
  Инициативность: {
    low: 'Вы предпочитаете действовать строго по инструкции. Развивайте проактивность: предлагайте идеи, даже если не уверены.',
    medium: 'Вы способны проявлять инициативу, но не всегда решаетесь. Чаще берите на себя роль лидера изменений.',
    high: 'Вы — генератор идей и драйвер изменений. Ваша инициативность помогает двигать проекты вперёд.'
  },
  Честность: {
    low: 'Склонность к компромиссам с правдой. Помните, что честность укрепляет доверие и репутацию в долгосрочной перспективе.',
    medium: 'Вы стараетесь быть честным, но в сложных ситуациях можете колебаться. Развивайте внутренний этический компас.',
    high: 'Высокая честность и принципиальность вызывают уважение. Вы способны отстаивать правду даже в трудных обстоятельствах.'
  },
  'Отношение к неудачам': {
    low: 'Неудачи вас сильно демотивируют. Работайте над стрессоустойчивостью и восприятием критики как источника роста.',
    medium: 'Вы справляетесь с неудачами, но они могут временно выбивать из колеи. Учитесь быстрее восстанавливаться.',
    high: 'Вы воспринимаете неудачи как ценный опыт. Ваша способность учиться на ошибках помогает постоянно совершенствоваться.'
  },
  'Работа в команде (проективная)': {
    low: 'Вы скорее индивидуалист. Поработайте над навыками коллаборации и доверия к коллегам.',
    medium: 'Вы умеете работать в команде, но предпочитаете чёткое разделение ответственности. Развивайте взаимовыручку.',
    high: 'Вы прирождённый командный игрок. С вами комфортно работать, вы создаёте атмосферу сотрудничества.'
  },
  'Мотивация достижения': {
    low: 'Вам не хватает амбиций. Поставьте перед собой вдохновляющие цели и разбейте их на конкретные шаги.',
    medium: 'У вас есть стремление к успеху, но оно нестабильно. Работайте над внутренней мотивацией.',
    high: 'Высокая мотивация достижения — ваш двигатель. Вы ставите амбициозные цели и упорно идёте к ним.'
  },
  'Отношение к правилам': {
    low: 'Вы слишком жёстко придерживаетесь правил, даже если они неэффективны. Развивайте гибкость и критическое мышление.',
    medium: 'Вы умеете балансировать между правилами и здравым смыслом. Хорошо, но иногда можно быть смелее.',
    high: 'Вы гибко подходите к регламентам, не нарушая их без необходимости. Ваш прагматизм помогает находить оптимальные решения.'
  },
  Эмпатия: {
    low: 'Вам стоит развивать эмоциональный интеллект. Учитесь слушать и понимать чувства окружающих.',
    medium: 'Вы способны к эмпатии, но не всегда её проявляете. Будьте внимательнее к эмоциональному состоянию коллег.',
    high: 'Вы обладаете высоким уровнем эмпатии. Люди чувствуют вашу поддержку и охотно делятся переживаниями.'
  }
};

// ------------------- компонент -------------------
const ProjectiveInterview: React.FC = () => {
  const navigate = useNavigate();
  const [currentScaleIndex, setCurrentScaleIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [submitted, setSubmitted] = useState(false);

  const allQuestions = useMemo(() => scales.flatMap(s => s.questions), []);

  const handleAnswer = (questionId: number, value: number) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const currentScale = scales[currentScaleIndex];
  const currentQuestions = currentScale.questions;
  const allAnswered = allQuestions.every(q => answers[q.id] !== undefined);

  const goNext = () => {
    if (currentScaleIndex < scales.length - 1) {
      setCurrentScaleIndex(prev => prev + 1);
    }
  };

  const goPrev = () => {
    if (currentScaleIndex > 0) {
      setCurrentScaleIndex(prev => prev - 1);
    }
  };

  const handleSubmit = () => setSubmitted(true);

  const scoresByScale = useMemo(() => {
    return scales.map(scale => {
      const total = scale.questions.reduce((sum, q) => sum + (answers[q.id] || 0), 0);
      const max = scale.questions.length * 5;
      const percentage = Math.round((total / max) * 100);
      return { title: scale.title, percentage, total };
    });
  }, [answers]);

  const radarData = scoresByScale.map(s => ({
    scale: s.title,
    score: s.percentage,
    fullMark: 100,
  }));

  // ---------- страница результатов ----------
  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.bgBlur1} />
        <div style={styles.bgBlur2} />

        <button onClick={() => navigate('/app/interview-assistant')} style={styles.backButton}>
          <ArrowLeft size={18} />
          <span>К помощнику интервью</span>
        </button>

        <div style={styles.header}>
          <h1 style={styles.title}>Результаты проективного интервью</h1>
          <p style={styles.subtitle}>Профиль на основе проективных ответов</p>
        </div>

        <div style={styles.resultsGrid}>
          <div style={styles.resultCard}>
            <h3 style={styles.chartTitle}><BarChart2 size={18} /> Профиль личности</h3>
            <ResponsiveContainer width="100%" height={350}>
              <RadarChart data={radarData} outerRadius={120}>
                <PolarGrid strokeDasharray="3 3" />
                <PolarAngleAxis dataKey="scale" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} />
                <Radar isAnimationActive={false} name="Уровень" dataKey="score" stroke="#6a11cb" fill="#6a11cb" fillOpacity={0.2} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          <div style={styles.resultCard}>
            <h3 style={styles.chartTitle}><Award size={18} /> Детализация по шкалам</h3>
            {scoresByScale.map(s => {
              const interp = interpretation[s.title];
              const level = s.percentage >= 70 ? 'high' : s.percentage >= 40 ? 'medium' : 'low';
              const color = level === 'high' ? '#2ecc71' : level === 'medium' ? '#f39c12' : '#e74c3c';
              return (
                <div key={s.title} style={styles.scaleRow}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: 15 }}>{s.title}</strong>
                    <span style={{ color, fontWeight: 600 }}>{s.percentage}%</span>
                  </div>
                  <div style={styles.progressBar}>
                    <div style={{ ...styles.progressFill, width: `${s.percentage}%`, background: color }} />
                  </div>
                  <p style={styles.interpretationText}>
                    {level === 'high' && interp.high}
                    {level === 'medium' && interp.medium}
                    {level === 'low' && interp.low}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <button
          onClick={() => { setSubmitted(false); setAnswers({}); setCurrentScaleIndex(0); }}
          style={styles.resetButton}
        >
          Пройти заново
        </button>
      </div>
    );
  }

  // ---------- вопросы ----------
  return (
    <div style={styles.container}>
      <div style={styles.bgBlur1} />
      <div style={styles.bgBlur2} />

      <button onClick={() => navigate('/app/interview-assistant')} style={styles.backButton}>
        <ArrowLeft size={18} />
        <span>К помощнику интервью</span>
      </button>

      <div style={styles.header}>
        <h1 style={styles.title}>Проективное интервью</h1>
        <p style={styles.subtitle}>
          Ответьте на проективные вопросы, чтобы выявить глубинные установки и поведенческие паттерны.
        </p>
      </div>

      <div style={styles.progressIndicator}>
        {scales.map((_, idx) => (
          <div
            key={idx}
            style={{
              ...styles.dot,
              background: idx <= currentScaleIndex ? '#6a11cb' : '#e0e0e0',
            }}
          />
        ))}
      </div>

      <div style={styles.card}>
        <h2 style={styles.scaleTitle}>
          <Target size={20} /> {currentScale.title}
        </h2>
        <p style={styles.scaleDesc}>{currentScale.description}</p>

        {currentQuestions.map(q => (
          <div key={q.id} style={styles.questionBlock}>
            <p style={styles.questionText}>{q.text}</p>
            <div style={styles.optionsGrid}>
              {q.options.map(opt => (
                <button
                  key={opt.value}
                  style={{
                    ...styles.optionButton,
                    background: answers[q.id] === opt.value ? 'linear-gradient(135deg, #6a11cb, #2575fc)' : 'rgba(255,255,255,0.5)',
                    color: answers[q.id] === opt.value ? 'white' : '#1a1a2e',
                    border: answers[q.id] === opt.value ? 'none' : '1px solid rgba(0,0,0,0.1)',
                  }}
                  onClick={() => handleAnswer(q.id, opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ))}

        <div style={styles.navButtons}>
          <button onClick={goPrev} disabled={currentScaleIndex === 0} style={styles.navButton}>
            Назад
          </button>
          <span style={{ color: '#666' }}>
            {currentScaleIndex + 1} / {scales.length}
          </span>
          {currentScaleIndex < scales.length - 1 ? (
            <button onClick={goNext} style={styles.navButton}>Далее</button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!allAnswered}
              style={{
                ...styles.navButton,
                background: allAnswered ? 'linear-gradient(135deg, #2ecc71, #27ae60)' : '#ccc',
                color: 'white',
                border: 'none',
              }}
            >
              Завершить и получить результат
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ------------------- стили -------------------
const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '80px 20px 20px 20px',
    background: 'linear-gradient(145deg, #f8fafd 0%, #e8f0fe 100%)',
    fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    position: 'relative',
    overflowX: 'clip',                     // вместо 'hidden', чтобы не создавать контекст скролла
    willChange: 'transform',              // ускоряет композитинг
    transform: 'translateZ(0)',           // создаёт отдельный слой

  },
  bgBlur1: {
    position: 'absolute', top: '-10%', left: '-5%', width: 600, height: 600,
    background: 'radial-gradient(circle, rgba(106,17,203,0.12) 0%, rgba(106,17,203,0) 70%)',
    borderRadius: '50%', filter: 'blur(60px)', zIndex: 0,
  },
  bgBlur2: {
    position: 'absolute', bottom: '-10%', right: '-5%', width: 700, height: 700,
    background: 'radial-gradient(circle, rgba(37,117,252,0.12) 0%, rgba(37,117,252,0) 70%)',
    borderRadius: '50%', filter: 'blur(60px)', zIndex: 0,
  },
  backButton: {
    position: 'absolute', top: 24, left: 24,
    background: 'rgba(255,255,255,0.6)',
    border: '1px solid rgba(0,0,0,0.06)',
    borderRadius: 40,
    padding: '10px 20px', fontSize: 15, fontWeight: 500, color: '#2575fc',
    cursor: 'pointer', transition: 'all 0.2s ease', zIndex: 10, display: 'inline-flex', alignItems: 'center', gap: 8,
  },
  header: { textAlign: 'center', marginBottom: 32, position: 'relative', zIndex: 1 },
  title: {
    fontSize: 36, fontWeight: 700, background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: '0 0 8px',
  },
  subtitle: { fontSize: 16, color: '#666', margin: 0 },
  progressIndicator: { display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 24, position: 'relative', zIndex: 1 },
  dot: { width: 12, height: 12, borderRadius: '50%', transition: 'background 0.3s' },
  card: {
    background: 'rgba(255,255,255,0.5)', borderRadius: 32,
    padding: '40px 32px', border: '1px solid rgba(0,0,0,0.06)',
    maxWidth: 800, margin: '0 auto', position: 'relative', zIndex: 1,
  },
  scaleTitle: { fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, color: '#1a1a2e' },
  scaleDesc: { fontSize: 15, color: '#4a4a68', marginBottom: 28 },
  questionBlock: { marginBottom: 28 },
  questionText: { fontSize: 16, fontWeight: 500, marginBottom: 12, color: '#1a1a2e' },
  optionsGrid: { display: 'flex', flexDirection: 'column', gap: 10 },
  optionButton: {
    textAlign: 'left' as const, padding: '14px 20px', borderRadius: 16, cursor: 'pointer',
    transition: 'all 0.2s', fontSize: 14, border: '1px solid rgba(0,0,0,0.1)',
  },
  navButtons: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 32 },
  navButton: {
    padding: '10px 24px', borderRadius: 30, border: '1px solid #2575fc', background: 'transparent',
    color: '#2575fc', cursor: 'pointer', fontWeight: 600, transition: 'all 0.2s',
  },
  resultsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: 28, maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 },
  resultCard: {
    background: 'rgba(255,255,255,0.5)', borderRadius: 32,
    padding: 32, border: '1px solid rgba(0,0,0,0.06)',
  },
  chartTitle: { fontSize: 20, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, color: '#1a1a2e' },
  scaleRow: { marginBottom: 20 },
  progressBar: { height: 8, background: '#e0e0e0', borderRadius: 4, marginTop: 6, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 4, transition: 'width 0.3s' },
  interpretationText: { fontSize: 13, color: '#555', marginTop: 6, lineHeight: 1.5 },
  resetButton: {
    marginTop: 40, display: 'block', marginLeft: 'auto', marginRight: 'auto',
    padding: '12px 28px', borderRadius: 30, background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600,
  },
};

export default ProjectiveInterview;